import React from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Check, X, ExternalLink } from "lucide-react";
import { getCasinoBySlug, CASINOS } from "../mock";
import Stars from "../components/Stars";

const NotFound = () => (
  <div className="mx-auto max-w-3xl px-5 py-24 text-center">
    <h1 className="text-2xl font-bold" style={{ color: "#ffffff" }}>
      Review not found
    </h1>
    <Link
      to="/"
      className="mt-6 inline-block text-sm font-semibold"
      style={{ color: "var(--cp-green)" }}
    >
      ← Back to Top Picks
    </Link>
  </div>
);

const SectionTitle = ({ children }) => (
  <h2 className="mb-4 text-xl font-bold sm:text-2xl" style={{ color: "#ffffff" }}>
    {children}
  </h2>
);

const PlayButton = ({ casino }) => (
  <a
    href={casino.playUrl}
    target="_blank"
    rel="noopener noreferrer"
    className="cp-play-btn inline-flex items-center justify-center gap-2 rounded-lg px-6 py-3 text-sm font-bold transition-all"
  >
    Play Now at {casino.name}
    <ExternalLink size={15} />
  </a>
);

const ReviewPage = () => {
  const { slug } = useParams();
  const casino = getCasinoBySlug(slug);

  if (!casino) return <NotFound />;

  const others = CASINOS.filter((c) => c.slug !== casino.slug);

  return (
    <div className="mx-auto max-w-4xl px-5 py-10">
      {/* Back link */}
      <Link
        to="/"
        className="cp-fade-up inline-flex items-center gap-2 text-sm font-medium transition-colors hover:text-white"
        style={{ color: "var(--cp-muted)" }}
      >
        <ArrowLeft size={16} />
        Back to Top Picks
      </Link>

      {/* Header */}
      <div className="cp-fade-up mt-8 flex flex-col items-center text-center">
        <div
          className="h-20 w-20 overflow-hidden rounded-xl"
          style={{ background: "rgb(10,11,13)" }}
        >
          <img
            src={casino.logo}
            alt={casino.name}
            className="h-full w-full object-cover"
          />
        </div>
        <h1 className="mt-5 text-3xl font-bold sm:text-4xl" style={{ color: "#ffffff" }}>
          {casino.name} Review
        </h1>
        <div className="mt-3 flex items-center gap-2 text-sm">
          <Stars rating={casino.rating} />
          <span style={{ color: "var(--cp-muted)" }}>· {casino.kind}</span>
        </div>
        <p
          className="mt-5 max-w-2xl text-sm leading-relaxed sm:text-base"
          style={{ color: "var(--cp-text)" }}
        >
          {casino.intro}
        </p>
        <p className="mt-4 max-w-2xl text-sm" style={{ color: "var(--cp-muted)" }}>
          <span style={{ color: "var(--cp-green)" }}>Current offer:</span>{" "}
          {casino.offer}
        </p>
        <div className="mt-6">
          <PlayButton casino={casino} />
        </div>
      </div>

      {/* Quick facts */}
      <div className="cp-fade-up mt-10 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {casino.quickFacts.map((f) => (
          <div
            key={f.label}
            className="rounded-xl border p-4"
            style={{ borderColor: "var(--cp-border)", background: "var(--cp-card)" }}
          >
            <p
              className="text-[10px] font-semibold uppercase tracking-widest"
              style={{ color: "var(--cp-muted)" }}
            >
              {f.label}
            </p>
            <p className="mt-1.5 text-sm font-medium" style={{ color: "#ffffff" }}>
              {f.value}
            </p>
          </div>
        ))}
      </div>

      {/* Overview */}
      <section className="mt-12">
        <SectionTitle>Overview</SectionTitle>
        <div className="flex flex-col gap-4">
          {casino.overview.map((p, i) => (
            <p key={i} className="text-sm leading-relaxed" style={{ color: "var(--cp-text)" }}>
              {p}
            </p>
          ))}
        </div>
      </section>

      {/* Pros & Cons */}
      <section className="mt-12">
        <SectionTitle>Pros &amp; Cons</SectionTitle>
        <div className="grid gap-4 md:grid-cols-2">
          <div
            className="rounded-2xl border p-5"
            style={{ borderColor: "var(--cp-border)", background: "var(--cp-card)" }}
          >
            <h3 className="mb-3 text-sm font-bold uppercase tracking-wide" style={{ color: "var(--cp-green)" }}>
              Pros
            </h3>
            <ul className="flex flex-col gap-3">
              {casino.pros.map((p, i) => (
                <li key={i} className="flex gap-3 text-sm" style={{ color: "var(--cp-text)" }}>
                  <Check size={16} className="mt-0.5 flex-shrink-0" style={{ color: "var(--cp-green)" }} />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
          <div
            className="rounded-2xl border p-5"
            style={{ borderColor: "var(--cp-border)", background: "var(--cp-card)" }}
          >
            <h3 className="mb-3 text-sm font-bold uppercase tracking-wide" style={{ color: "#ef5a5a" }}>
              Cons
            </h3>
            <ul className="flex flex-col gap-3">
              {casino.cons.map((c, i) => (
                <li key={i} className="flex gap-3 text-sm" style={{ color: "var(--cp-text)" }}>
                  <X size={16} className="mt-0.5 flex-shrink-0" style={{ color: "#ef5a5a" }} />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Key facts */}
      <section className="mt-12">
        <SectionTitle>Key Facts</SectionTitle>
        <div
          className="overflow-hidden rounded-2xl border"
          style={{ borderColor: "var(--cp-border)" }}
        >
          {casino.keyFacts.map((f, i) => (
            <div
              key={f.label}
              className="flex flex-col gap-1 px-5 py-3.5 sm:flex-row sm:justify-between"
              style={{
                background: i % 2 === 0 ? "var(--cp-card)" : "rgba(255,255,255,0.015)",
                borderTop: i === 0 ? "none" : "1px solid var(--cp-border)",
              }}
            >
              <span className="text-sm font-semibold" style={{ color: "#ffffff" }}>
                {f.label}
              </span>
              <span className="text-sm sm:text-right" style={{ color: "var(--cp-muted)" }}>
                {f.value}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* Games & Providers */}
      <section className="mt-12">
        <SectionTitle>Games &amp; Providers</SectionTitle>
        <ul className="flex flex-col gap-3">
          {casino.games.map((g, i) => (
            <li key={i} className="flex gap-3 text-sm" style={{ color: "var(--cp-text)" }}>
              <span style={{ color: "var(--cp-green)" }}>•</span>
              <span>{g}</span>
            </li>
          ))}
        </ul>
        <div className="mt-5 flex flex-wrap gap-2">
          {casino.providers.map((p) => (
            <span
              key={p}
              className="rounded-full border px-3 py-1 text-xs font-medium"
              style={{ borderColor: "var(--cp-border)", color: "var(--cp-text)", background: "var(--cp-card)" }}
            >
              {p}
            </span>
          ))}
        </div>
      </section>

      {/* Verdict */}
      <section className="mt-12">
        <SectionTitle>Final Verdict</SectionTitle>
        <div
          className="rounded-2xl border p-6"
          style={{
            borderColor: "rgba(34,224,107,0.25)",
            background: "rgba(34,224,107,0.04)",
          }}
        >
          <p className="text-sm leading-relaxed" style={{ color: "var(--cp-text)" }}>
            {casino.verdict}
          </p>
          <div className="mt-5">
            <PlayButton casino={casino} />
          </div>
        </div>
      </section>

      {/* Compare */}
      <section className="mt-12">
        <SectionTitle>Compare With Other Sites</SectionTitle>
        <div className="flex flex-wrap gap-3">
          {others.map((c) => (
            <Link
              key={c.slug}
              to={`/${c.slug}`}
              className="cp-review-btn rounded-lg border px-4 py-2.5 text-sm font-semibold transition-colors"
            >
              {c.name} review
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
};

export default ReviewPage;
